/**
 * @file Index.tsx the react container for the sidebar application
 * @author Jonathan Sommer <j.sommer@fc-gruppe.de>
 * @copyright (c) FC-Digitalisierung GmbH 2020
 * @license All rights reserved.
 */

//import "office-ui-fabric-react/dist/css/fabric.min.css";
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import 'moment/locale/de';
import * as ReactDOM from 'react-dom';
import App from './apps/main/App';
import './i18n';
import { AppContext } from './models/Types';
import { configuration, logout } from './modules/auth/authUtils';
import * as serviceWorker from './serviceWorker';
import * as ConfigValues from './util/configValues';
import * as OfficeJs from './util/officeJs';
let isOfficeInitialized = false;
let appContext: AppContext = 'browser';
const title = 'PRIO';

const pca = new PublicClientApplication(configuration);

// Component
const AppProvider = () => (
  <MsalProvider instance={pca}>
    <App title={title} isOfficeInitialized={isOfficeInitialized} />
  </MsalProvider>
);

const render = () =>
  ReactDOM.render(<AppProvider />, document.getElementById('root'));

/* Render application after Office initializes */
(async () => {
  if (ConfigValues.REACT_APP_ENVIRONMENT === 'office') {
    const tryOffice = () => {
      return new Promise<{
        host: Office.HostType;
        platform: Office.PlatformType;
      }>(async (resolve) => {
        try {
          const officeJsResult = await Office.onReady();
          resolve(officeJsResult);
        } catch {
          resolve(null);
        }
      });
    };
    const retry = (
      officeResult: { host: Office.HostType; platform: Office.PlatformType },
      times: number,
      milliseconds: number
    ) => {
      return new Promise<{
        host: Office.HostType;
        platform: Office.PlatformType;
      }>(async (resolve) => {
        if (officeResult) {
          resolve(officeResult);
        } else if (times > 0) {
          const timer = setTimeout(async () => {
            clearTimeout(timer);
            await tryOffice().then((newResult) => {
              resolve(retry(newResult, times - 1, milliseconds));
            });
          }, milliseconds);
        } else {
          resolve(null);
        }
      });
    };
    await tryOffice()
      .then((officeResult) => {
        return retry(officeResult, 4, 500);
      })
      .then((res) => {
        if (res.host === Office.HostType.Outlook) {
          appContext = 'outlook';
          OfficeJs.setOutlookContext(true);
          console.info('initialize done - Outlook AddIn');
        } else {
          console.warn(
            'PRIO started from office context, but not Outlook. This should not happen...'
          );
        }
      });
  } else if (ConfigValues.REACT_APP_ENVIRONMENT === 'teams') {
    appContext = 'teams';
    OfficeJs.setTeamsContext(true);
    console.info('initialize done - Teams Application');
  } else {
    appContext = 'browser';
    console.info('initialize done - Browser Application');
  }

  OfficeJs.setAppContext(appContext);

  isOfficeInitialized = true;

  if (window.location.pathname.startsWith('/logout/')) {
    logout();
  } else {
    render();
  }
})();

serviceWorker.register({
  onUpdate: (registration) => {
    const waitingServiceWorker = registration.waiting;

    if (waitingServiceWorker) {
      waitingServiceWorker.addEventListener('statechange', (event: any) => {
        if (event.target.state === 'activated') {
          window.location.reload();
        }
      });
    }
  },
});
